import { type ApolloError, isApolloError } from '@apollo/client';
import { type FallbackRender, captureException } from '@sentry/react';
import posthog from 'posthog-js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { redirectToAuth } from 'supertokens-auth-react';
import Session from 'supertokens-auth-react/recipe/session';

import {
  Button,
  Heading,
  LoadingIcon,
  Stack,
  Text,
  toast,
} from '@eluve/components';
import { useNamedLogger } from '@eluve/logger';
import { extractTenantAndAppointmentIdsFromPath } from '@eluve/utils';

import { ErrorPage } from './pages/ErrorPage';

export type ErrorFallbackSurface = 'web-vite' | 'reviewer-tools' | 'extension';

/**
 * Browser-thrown messages for transient `fetch()` failures (network blip,
 * captive portal, offline). Chromium uses 'Failed to fetch'; Safari uses
 * 'Load failed'. Outside our control, so we log at warn instead of error.
 */
const TRANSIENT_NETWORK_ERROR_MESSAGES = new Set([
  'Failed to fetch',
  'Load failed',
]);

/**
 * Error messages that indicate an authentication/session issue.
 * These are errors returned by Hasura when the JWT is invalid or expired.
 */
const AUTH_ERROR_MESSAGES = ['JWTExpired', 'Malformed Authorization header'];

type TokenAcquisitionErrorReason = 'tenant-switch-failed' | 'token-timeout';

type ErrorWithMetadata = Error & {
  metadata?: Record<string, unknown>;
};

/**
 * Determines if an Apollo error is related to authentication/session issues.
 * Only these errors should trigger a sign-out, as they indicate the user's
 * session is no longer valid.
 *
 * Other Apollo errors (network issues, validation errors, etc.) should NOT
 * trigger a sign-out as they are unrelated to the user's authentication state.
 */
const isAuthenticationError = (error: ApolloError): boolean => {
  const hasAuthErrorMessage = error.graphQLErrors?.some((graphQLError) =>
    AUTH_ERROR_MESSAGES.some((authMessage) =>
      graphQLError.message.includes(authMessage),
    ),
  );

  return hasAuthErrorMessage;
};

const isTokenAcquisitionErrorReason = (
  reason: unknown,
): reason is TokenAcquisitionErrorReason => {
  return reason === 'tenant-switch-failed' || reason === 'token-timeout';
};

const getTokenAcquisitionErrorReason = (
  error: ApolloError,
): TokenAcquisitionErrorReason | null => {
  const networkError = error.networkError as ErrorWithMetadata | null;
  if (!networkError) {
    return null;
  }

  if (networkError.name !== 'TokenAcquisitionError') {
    return null;
  }

  const reason = networkError.metadata?.reason;
  if (isTokenAcquisitionErrorReason(reason)) {
    return reason;
  }

  return null;
};

/**
 * Narrows an unknown error to an ApolloError if applicable.
 * Returns null if the error is not an ApolloError.
 */
const asApolloError = (error: unknown): ApolloError | null => {
  if (error instanceof Error && isApolloError(error)) {
    return error;
  }
  return null;
};

type Props = Parameters<FallbackRender>[0] & {
  surface: ErrorFallbackSurface;
};

const reconnectPageClassName =
  'grid min-h-full place-items-center bg-white px-6 py-24 sm:py-32 lg:px-8';

const MAX_CONSECUTIVE_AUTO_RECONNECTS = 3;
// A gap longer than this means the prior incident recovered; the next failure
// starts with a fresh auto-reconnect budget.
const AUTO_RECONNECT_WINDOW_MS = 30_000;

// Module-scoped so the budget survives the boundary remounting a fresh
// TokenReconnect on each resetError(). Guards the asymmetric-failure case where
// the session refresh succeeds but token acquisition keeps timing out — without
// a cross-remount bound, resetError() → re-throw → remount → auto-attempt would
// loop indefinitely.
let autoReconnectAttempts = 0;
let lastAutoReconnectAt = 0;

/**
 * Scoped recovery UI for a transient `TokenAcquisitionError` (reason
 * `token-timeout`). On mount it makes one automatic attempt to refresh the
 * SuperTokens session; a successful refresh means we have a valid session
 * again, so `resetError()` unwinds the boundary and token acquisition retries
 * against the fresh session. A failed refresh surfaces a manual retry / sign-in
 * affordance instead of reset-looping against a still-broken session.
 */
const TokenReconnect = ({ resetError }: { resetError: () => void }) => {
  const [status, setStatus] = useState<'reconnecting' | 'failed'>(
    'reconnecting',
  );
  const hasAutoAttemptedRef = useRef(false);

  const attemptReconnect = useCallback(() => {
    setStatus('reconnecting');
    Session.attemptRefreshingSession()
      .then((refreshed) => {
        if (refreshed) {
          resetError();
        } else {
          setStatus('failed');
        }
      })
      .catch(() => setStatus('failed'));
  }, [resetError]);

  useEffect(() => {
    if (hasAutoAttemptedRef.current) {
      return;
    }
    hasAutoAttemptedRef.current = true;

    const now = Date.now();
    if (now - lastAutoReconnectAt > AUTO_RECONNECT_WINDOW_MS) {
      autoReconnectAttempts = 0;
    }
    lastAutoReconnectAt = now;

    // Exhausted the auto-reconnect budget for this incident — stop
    // auto-refreshing and hand off to the manual affordance so we can't
    // reset-loop against a persistently-broken token path.
    if (autoReconnectAttempts >= MAX_CONSECUTIVE_AUTO_RECONNECTS) {
      setStatus('failed');
      return;
    }
    autoReconnectAttempts += 1;
    attemptReconnect();
  }, [attemptReconnect]);

  const handleSignIn = useCallback(() => {
    Session.signOut()
      .then(() => redirectToAuth({ redirectBack: true }))
      .catch((signOutError) => {
        captureException(signOutError);
        toast.error('Failed to sign out. Please try again.');
      });
  }, []);

  if (status === 'reconnecting') {
    return (
      <main className={reconnectPageClassName}>
        <Stack gap={4} align="center" className="text-center">
          <LoadingIcon size="3xl" className="text-text-accent-primary" />
          <Heading>Reconnecting…</Heading>
          <Text size="md" color="tertiary" className="leading-7">
            We hit a snag reaching Eluve. Reconnecting you now.
          </Text>
        </Stack>
      </main>
    );
  }

  return (
    <main className={reconnectPageClassName}>
      <Stack gap={4} align="center" className="text-center">
        <Heading>We're having trouble reconnecting</Heading>
        <Text size="md" color="tertiary" className="leading-7">
          Your connection to Eluve was interrupted. You can try again, or sign
          in to continue.
        </Text>
        <Stack direction="horizontal" gap={3} align="center" justify="center">
          <Button variant="outline" onClick={attemptReconnect}>
            Try again
          </Button>
          <Button onClick={handleSignIn}>Sign in again</Button>
        </Stack>
      </Stack>
    </main>
  );
};

export const ErrorFallbackRender = ({
  error,
  componentStack,
  resetError,
  surface,
}: Props) => {
  const apolloError = asApolloError(error);
  const tokenAcquisitionErrorReason = apolloError
    ? getTokenAcquisitionErrorReason(apolloError)
    : null;
  const logger = useNamedLogger('error-fallback');
  const hasLoggedRef = useRef(false);
  const hasResetTenantSwitchErrorRef = useRef(false);

  // Idempotent via hasLoggedRef. Called from both useEffect (normal path) and
  // the auth-error branch below — `resetError()` there flips the boundary
  // synchronously, and the passive effect can race the unmount under
  // concurrent rendering, so we log eagerly to guarantee delivery.
  const logRenderError = useCallback(() => {
    if (hasLoggedRef.current) {
      return;
    }
    hasLoggedRef.current = true;

    const errorObject =
      error instanceof Error ? error : new Error(String(error));
    const { tenantId, appointmentId } =
      typeof window !== 'undefined'
        ? extractTenantAndAppointmentIdsFromPath(window.location.pathname)
        : { tenantId: undefined, appointmentId: undefined };

    // A transient token-acquisition timeout is recoverable (see the reconnect
    // branch below) and driven by upstream auth instability, not a code bug —
    // log it at warn like the other transient-network messages.
    const isRecoverableError =
      tokenAcquisitionErrorReason === 'token-timeout' ||
      TRANSIENT_NETWORK_ERROR_MESSAGES.has(errorObject.message);
    const log = isRecoverableError ? logger.warn : logger.error;
    log('React render error caught by ErrorFallback', {
      error: errorObject,
      componentStack,
      surface,
      tenantId,
      appointmentId,
    });
  }, [error, componentStack, surface, logger, tokenAcquisitionErrorReason]);

  useEffect(() => {
    logRenderError();
  }, [logRenderError]);

  useEffect(() => {
    if (
      tokenAcquisitionErrorReason !== 'tenant-switch-failed' ||
      hasResetTenantSwitchErrorRef.current
    ) {
      return;
    }

    hasResetTenantSwitchErrorRef.current = true;
    logRenderError();
    resetError();
  }, [tokenAcquisitionErrorReason, logRenderError, resetError]);

  if (tokenAcquisitionErrorReason === 'tenant-switch-failed') {
    // FallbackRender requires Element to be returned
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  }

  // A transient token-acquisition timeout (usually upstream SuperTokens
  // instability) should not crash the whole page to ErrorPage. Offer a scoped
  // reconnect path that refreshes the session and unwinds the boundary.
  if (tokenAcquisitionErrorReason === 'token-timeout') {
    return <TokenReconnect resetError={resetError} />;
  }

  // Only sign out for authentication-related Apollo errors
  if (apolloError && isAuthenticationError(apolloError)) {
    logRenderError();
    resetError();
    captureException(error);
    toast.error(
      'Your session has expired. Please re-authenticate to continue using Eluve.',
    );

    Session.signOut()
      .then(() => redirectToAuth({ redirectBack: true }))
      .catch((signOutError) => {
        captureException(signOutError);
        toast.error('Failed to sign out. Please try again.');
      });
    // FallbackRender requires Element to be returned
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  }

  // Track when error page is displayed for monitoring
  captureException(error);
  posthog.capture('error_page_displayed', {
    errorType: apolloError ? 'apollo' : 'generic',
    errorMessage: error instanceof Error ? error.message : String(error),
    isNetworkError: apolloError ? Boolean(apolloError.networkError) : false,
  });

  // For all other errors (including non-auth Apollo errors), show the error page
  return <ErrorPage reset={resetError} />;
};
